import { useSyncExternalStore } from 'react';
import { downloadDataAsExcel } from '../../utilities/downloadExcel';
import { getErrorMessage } from '../../utilities/commonFunctions';

const downloadLoadingByFileName = new Map();
const downloadLoadingListenersByKey = new Map();

const getDownloadStateKey = fileName => {
  if (typeof fileName !== 'string') {
    return '';
  }

  const timestampedMatch = fileName.match(
    /^(.*)_\d{4}-\d{2}-\d{2}_\d{6}(\.[^.]+)?$/
  );
  if (timestampedMatch) {
    return timestampedMatch[1];
  }

  return fileName.replace(/\.[^.]+$/, '');
};

const getDownloadLoadingSnapshot = downloadKey =>
  downloadLoadingByFileName.get(downloadKey) ?? false;

const subscribeToDownloadLoading = (downloadKey, callback) => {
  if (!downloadLoadingListenersByKey.has(downloadKey)) {
    downloadLoadingListenersByKey.set(downloadKey, new Set());
  }

  const listeners = downloadLoadingListenersByKey.get(downloadKey);
  listeners.add(callback);

  return () => {
    listeners.delete(callback);
    if (listeners.size === 0) {
      downloadLoadingListenersByKey.delete(downloadKey);
    }
  };
};

const setDownloadLoadingState = (downloadKey, isLoading) => {
  downloadLoadingByFileName.set(downloadKey, isLoading);
  const listeners = downloadLoadingListenersByKey.get(downloadKey);
  listeners?.forEach(listener => listener());
};

export const useDownloadManager = (
  downloadFunction,
  fileName,
  addErrorMessage,
  formatOptions
) => {
  const downloadKey = getDownloadStateKey(fileName);
  const defaultFormatOptions = {
    enableFormatting: true,
    formatHeaders: true,
    selectedColumns: undefined,
  };

  const opts = { ...defaultFormatOptions, ...(formatOptions || {}) };
  const downloadLoading = useSyncExternalStore(
    callback => subscribeToDownloadLoading(downloadKey, callback),
    () => getDownloadLoadingSnapshot(downloadKey),
    () => false
  );

  const handleDownload = async filters => {
    setDownloadLoadingState(downloadKey, true);
    try {
      const { rowCount } = await downloadDataAsExcel({
        formatOptions: opts,
        fileBaseName: fileName.replace(/\.(csv|xlsx)$/i, ''),
        timestampAsDate: true,
        fetchPage: async (pageNumber, pageSize) => {
          const { data } = await downloadFunction(
            pageNumber,
            pageSize,
            filters,
            { skipRouteAbort: true }
          );
          return {
            csvData: data?.csv_data,
            nextPageNumber: data?.next_page_number,
          };
        },
      });

      if (rowCount === 0) {
        addErrorMessage('No data available to download.');
        return;
      }
      addErrorMessage('Successfully downloaded', 'success');
    } catch (error) {
      addErrorMessage(
        getErrorMessage(
          error,
          'Unable to download logs! Please try again after sometime.'
        )
      );
    } finally {
      setDownloadLoadingState(downloadKey, false);
    }
  };

  return {
    downloadLoading,
    handleDownload,
  };
};
